import { TS, WW, WH, PW, PH, GRAV, MOB_TYPES, SOLID, LIQUID, T, ARMOR_TIERS, WEAPON_TIERS } from './constants';
import { MobRenderer } from './MobRenderer';
import { sfx } from './Sound';

let mobIdCounter = 0;
export class Mob {
    constructor(type, x, y) {
        const def = MOB_TYPES[type]; this.type = type; this.def = def; this.id = mobIdCounter++;
        this.x = x; this.y = y; this.vx = 0; this.vy = 0; this.hp = def.hp; this.maxHp = def.hp;
        this.w = def.w; this.h = def.h; this.ground = false; this.right = Math.random() > 0.5;
        this.ai = {
            state: 'idle_stand', stateTimer: 0, idleInterval: 1500 + Math.random() * 2500,
            walkDir: Math.random() > 0.5 ? 1 : -1, jumpCd: 0, attackCd: 0,
            leapCd: 0, strafeCd: 0, strafeDir: 1,
            circleAngle: Math.random() * Math.PI * 2,
            retreatTimer: 0, ambushTimer: 0,
            lastPlayerX: 0, lastPlayerY: 0, playerVx: 0, playerVy: 0,
            homeX: x, homeY: y, patrolDist: 60 + Math.random() * 100,
            idleActivity: 0, headBob: 0,
            restlessness: 0.3 + Math.random() * 0.7,
            personality: Math.floor(Math.random() * 4)
        };
        this.hitTimer = 0; this.dead = false; this.squash = 1; this.stretch = 1;
        this.colorIndex = Math.floor(Math.random() * 4);
        // Worm segments
        if (def.isWorm) {
            this.segments = [];
            for (let i = 0; i < 8; i++) this.segments.push({ x: x + this.w / 2, y: y + this.h / 2 });
        }
        // Villagers start wandering immediately
        if (def.isVillager) {
            this.ai.idleInterval = 300 + Math.random() * 600;
            this.ai.stateTimer = Math.random() * 1500;
            this.ai.profession = Math.floor(Math.random() * 7);
            this.ai.speechTimer = 2000 + Math.random() * 5000;
            this.ai.speechText = '';
            this.ai.speechShow = 0;
            this.ai.isNight = false;
            this.ai.fleeFromMob = false;
            this.ai.questGiven = false;
            this.ai.questId = -1;
            const vs = Math.random();
            if (vs > 0.5) { this.ai.state = 'v_walk'; this.ai.walkDir = Math.random() > 0.5 ? 1 : -1; this.right = this.ai.walkDir > 0; }
            else if (vs > 0.25) { this.ai.state = 'v_work'; }
            else { this.ai.state = 'v_inspect'; }
        }
    }

    // Check if there's a gap (no ground) ahead
    _gapAhead(world, dir) {
        const tx = Math.floor((this.x + this.w / 2 + dir * TS * 1.5) / TS);
        const ty = Math.floor((this.y + this.h) / TS);
        return !SOLID.has(world.gt(tx, ty + 1)) && !SOLID.has(world.gt(tx, ty + 2));
    }

    // Check if there's a wall ahead
    _wallAhead(world, dir) {
        const tx = Math.floor((this.x + this.w / 2 + dir * TS) / TS);
        const ty = Math.floor((this.y + this.h / 2) / TS);
        return SOLID.has(world.gt(tx, ty)) || SOLID.has(world.gt(tx, ty - 1));
    }

    update(dt, world, player) {
        if (this.dead) return; const def = this.def;
        const dx = (player.x + PW / 2) - (this.x + this.w / 2), dy = (player.y + PH / 2) - (this.y + this.h / 2);
        const dist = Math.sqrt(dx * dx + dy * dy) / TS;
        this.ai.stateTimer += dt;
        this.ai.jumpCd = Math.max(0, this.ai.jumpCd - dt);
        this.ai.attackCd = Math.max(0, this.ai.attackCd - dt);
        this.ai.leapCd = Math.max(0, this.ai.leapCd - dt);
        this.ai.strafeCd = Math.max(0, this.ai.strafeCd - dt);
        this.hitTimer = Math.max(0, this.hitTimer - dt);
        this.squash += (1 - this.squash) * 0.15; this.stretch += (1 - this.stretch) * 0.15;

        // Track player velocity for prediction
        const px2 = player.x + PW / 2, py2 = player.y + PH / 2;
        if (this.ai.lastPlayerX !== 0) {
            this.ai.playerVx = (px2 - this.ai.lastPlayerX) * 0.3 + this.ai.playerVx * 0.7;
            this.ai.playerVy = (py2 - this.ai.lastPlayerY) * 0.3 + this.ai.playerVy * 0.7;
        }
        this.ai.lastPlayerX = px2; this.ai.lastPlayerY = py2;

        // Low HP retreat for smart mobs
        const hpRatio = this.hp / this.maxHp;
        const isSmart = ['WOLF', 'JAGUAR', 'LION', 'SKELETON', 'DEMON', 'SHADOW', 'CAVE_WORM', 'DEEP_EYE', 'SKELETON_MINER', 'CHRONOLISK', 'LIGHT_WEAVER'].includes(this.type);

        // === VILLAGER NIGHT / DANGER DETECTION ===
        if (def.isVillager) {
            // Check for nearby hostile mobs
            const g = (typeof window._gameRef !== 'undefined') ? window._gameRef : null;
            if (g) {
                this.ai.isNight = g.dayTime < 0.25 || g.dayTime > 0.75;
                // Flee from hostile mobs nearby
                let nearestEnemy = null, nearestEDist = Infinity;
                for (const m of g.mobs) {
                    if (m === this || m.dead || m.def.passive || m.def.isVillager) continue;
                    const edx = m.x - this.x, edy = m.y - this.y;
                    const ed = Math.sqrt(edx * edx + edy * edy) / TS;
                    if (ed < 8 && ed < nearestEDist) { nearestEnemy = m; nearestEDist = ed; }
                }
                if (nearestEnemy && this.ai.state !== 'v_flee') {
                    this.ai.state = 'v_flee'; this.ai.stateTimer = 0;
                    this.ai.fleeFromMob = nearestEnemy;
                    // Shout for help
                    this.ai.speechText = ['Помогите!', 'Монстры!', 'На помощь!!', 'Спасите!', 'Бежим!!!'][Math.floor(Math.random() * 5)];
                    this.ai.speechShow = 2000;
                }
                // Night behavior — go home
                if (this.ai.isNight && !nearestEnemy && this.ai.state !== 'v_flee' && this.ai.state !== 'v_sleep') {
                    const distHome = Math.abs(this.x - this.ai.homeX);
                    if (distHome > 60) {
                        if (this.ai.state !== 'v_gohome') { this.ai.state = 'v_gohome'; this.ai.stateTimer = 0; }
                    } else if (this.ai.state !== 'v_sleep' && this.ai.stateTimer > 2000) {
                        this.ai.state = 'v_sleep'; this.ai.stateTimer = 0;
                    }
                }
                // Wake up in morning
                if (!this.ai.isNight && this.ai.state === 'v_sleep') {
                    this.ai.state = 'idle_stand'; this.ai.stateTimer = 0;
                    this.ai.speechText = ['Доброе утро!', 'Новый день!', 'Пора за работу!'][Math.floor(Math.random() * 3)];
                    this.ai.speechShow = 2000;
                }
            }
            // Speech timer
            this.ai.speechShow = Math.max(0, this.ai.speechShow - dt);
            this.ai.speechTimer -= dt;
            if (this.ai.speechTimer <= 0 && this.ai.speechShow <= 0) {
                this.ai.speechTimer = 4000 + Math.random() * 8000;
                // Contextual speech
                const profNames = ['blacksmith', 'farmer', 'guard', 'librarian', 'cook', 'mason', 'herbalist'];
                const prof = profNames[this.ai.profession] || 'idle';
                const phrases = this.ai.isNight ?
                    ['Спокойной ночи...', 'Темнеет...', 'Ночь на дворе!', 'Пора спать...'] :
                    (dist < 6 ?
                        ['Привет!', 'Добро пожаловать!', 'Здравствуй!', 'Рады видеть!'] :
                        { blacksmith: ['Металл крепок!', '*стук-стук*'], farmer: ['Урожай растёт!', 'Поливаю...'], guard: ['Стою на страже.', 'Всё спокойно.'], librarian: ['Знание — сила.', 'Тише...'], cook: ['Вкуснятина!', 'Суп готов!'], mason: ['Камень к камню.', 'Строю!'], herbalist: ['Нужны травы?', 'Природа лечит.'] }[prof] || ['Хм...', 'Чудесный день!', '...']);
                const pArr = Array.isArray(phrases) ? phrases : ['...'];
                this.ai.speechText = pArr[Math.floor(Math.random() * pArr.length)];
                this.ai.speechShow = 2500;
            }
        }

        // Guard combat AI — chase hostile mobs near village
        if (def.isGuard) {
            const g = (typeof window._gameRef !== 'undefined') ? window._gameRef : null;
            if (g) {
                let nearestEnemy = null, nearestEDist = Infinity;
                for (const m of g.mobs) {
                    if (m === this || m.dead || m.def.passive || m.def.isVillager) continue;
                    const edx = m.x - this.x, edy = m.y - this.y;
                    const ed = Math.sqrt(edx * edx + edy * edy) / TS;
                    if (ed < def.sight && ed < nearestEDist) { nearestEnemy = m; nearestEDist = ed; }
                }
                if (nearestEnemy) {
                    this.ai._guardTarget = nearestEnemy;
                    if (this.ai.state !== 'g_chase') { this.ai.state = 'g_chase'; this.ai.stateTimer = 0; }
                } else if (this.ai.state === 'g_chase') {
                    this.ai.state = 'idle_stand'; this.ai.stateTimer = 0;
                    this.ai._guardTarget = null;
                }
                // Guard deals damage to nearby enemies
                if (this.ai._guardTarget && !this.ai._guardTarget.dead && this.ai.attackCd <= 0) {
                    const gt = this.ai._guardTarget;
                    const gdx = Math.abs(gt.x - this.x), gdy = Math.abs(gt.y - this.y);
                    if (gdx < 20 && gdy < 20) {
                        gt.hp -= def.dmg;
                        gt.hitTimer = 200;
                        this.ai.attackCd = 800;
                        gt.vx += (gt.x > this.x ? 4 : -4);
                        gt.vy -= 2;
                        if (gt.hp <= 0) { gt.dead = true; this.ai._guardTarget = null; }
                    }
                }
            }
        }

        // State transitions
        if (def.passive || def.isGuard) {
            if (!def.isVillager && !def.isGuard && dist < 5 && this.ai.state !== 'flee') { this.ai.state = 'flee'; this.ai.stateTimer = 0; }
            else if (!def.isVillager && !def.isGuard && dist >= 7 && this.ai.state === 'flee') { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; }
        } else if (def.neutral) {
            if (this.hp < this.maxHp && dist < def.sight && this.ai.state !== 'chase' && this.ai.state !== 'retreat') { this.ai.state = 'chase'; this.ai.stateTimer = 0; }
            else if (this.hp >= this.maxHp && dist >= def.sight * 0.8 && this.ai.state === 'chase') { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; }
        } else {
            if (dist < def.sight && this.ai.state !== 'chase' && this.ai.state !== 'leap_attack' && this.ai.state !== 'retreat' && this.ai.state !== 'circle') {
                this.ai.state = 'chase'; this.ai.stateTimer = 0;
            }
            else if (dist >= def.sight * 1.3 && (this.ai.state === 'chase' || this.ai.state === 'circle')) { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; }
            // Retreat when low HP (smart mobs)
            if (isSmart && hpRatio < 0.25 && this.ai.state === 'chase' && dist < 6 && Math.random() > 0.97) {
                this.ai.state = 'retreat'; this.ai.stateTimer = 0; this.ai.retreatTimer = 800 + Math.random() * 600;
            }
            // Leap attack transition for fast ground mobs
            if (def.ground && def.spd >= 2 && dist < 8 && dist > 3 && this.ground && this.ai.leapCd <= 0 && this.ai.state === 'chase' && Math.random() > 0.985) {
                this.ai.state = 'leap_attack'; this.ai.stateTimer = 0;
            }
            // Flying mobs circle when close
            if (!def.ground && dist < 6 && this.ai.state === 'chase' && Math.random() > 0.99) {
                this.ai.state = 'circle'; this.ai.stateTimer = 0;
            }
        }

        // Execute state
        switch (this.ai.state) {
            case 'idle_stand':
                this.vx *= 0.85;
                this.ai.headBob = Math.sin(this.ai.stateTimer / 600) * 0.5;
                if (this.ai.stateTimer > this.ai.idleInterval) {
                    const r = Math.random();
                    if (def.isVillager) {
                        if (r > 0.72) { this.ai.state = 'v_walk'; this.ai.walkDir = Math.random() > 0.5 ? 1 : -1; this.right = this.ai.walkDir > 0; }
                        else if (r > 0.52) { this.ai.state = 'v_work'; }
                        else if (r > 0.38) { this.ai.state = 'v_inspect'; }
                        else if (r > 0.24) { this.ai.state = 'v_chat'; }
                        else if (r > 0.14) { this.ai.state = 'idle_look'; }
                        else if (r > 0.06) { this.ai.state = 'v_sweep'; this.ai.walkDir = Math.random() > 0.5 ? 1 : -1; }
                        else { this.ai.state = 'v_sit'; }
                        this.ai.stateTimer = 0; this.ai.idleInterval = 300 + Math.random() * 700;
                    } else if (def.passive) {
                        if (r > 0.62) { this.ai.state = 'idle_walk'; this.ai.walkDir = Math.random() > 0.5 ? 1 : -1; this.right = this.ai.walkDir > 0; }
                        else if (r > 0.42) { this.ai.state = 'idle_graze'; }
                        else if (r > 0.25) { this.ai.state = 'idle_hop'; this.ai.idleActivity = 0; }
                        else if (r > 0.12) { this.ai.state = 'idle_groom'; }
                        else { this.ai.state = 'idle_look'; }
                        this.ai.stateTimer = 0; this.ai.idleInterval = 800 + Math.random() * 1500;
                    } else if (!def.ground) {
                        if (r > 0.5) { this.ai.state = 'idle_fly_circle'; }
                        else if (r > 0.28) { this.ai.state = 'idle_swoop'; }
                        else if (r > 0.1) { this.ai.state = 'idle_perch'; }
                        else { this.ai.state = 'idle_look'; }
                        this.ai.stateTimer = 0; this.ai.idleInterval = 1000 + Math.random() * 2000;
                    } else {
                        if (this.type === 'SLIME' && r > 0.45) { this.ai.state = 'idle_hop'; this.ai.idleActivity = 0; }
                        else if (r > 0.52) { this.ai.state = 'idle_patrol'; this.ai.walkDir = Math.random() > 0.5 ? 1 : -1; this.right = this.ai.walkDir > 0; this.ai.idleActivity = 0; }
                        else if (r > 0.36) { this.ai.state = 'idle_sniff'; }
                        else if (r > 0.22) { this.ai.state = 'idle_walk'; this.ai.walkDir = Math.random() > 0.5 ? 1 : -1; this.right = this.ai.walkDir > 0; }
                        else if (r > 0.12) { this.ai.state = 'idle_scratch'; }
                        else if (r > 0.04 && this.ai.personality === 3) { this.ai.state = 'idle_rest'; }
                        else { this.ai.state = 'idle_look'; }
                        this.ai.stateTimer = 0; this.ai.idleInterval = 1200 + Math.random() * 2000;
                    }
                } break;
            case 'idle_walk': {
                const walkMult = def.isVillager ? 0.7 : (def.passive ? 0.4 : 0.35);
                this.vx = this.ai.walkDir * def.spd * walkMult; this.right = this.ai.walkDir > 0;
                if (this._wallAhead(world, this.ai.walkDir) || this._gapAhead(world, this.ai.walkDir)) { this.ai.walkDir = -this.ai.walkDir; this.right = this.ai.walkDir > 0; }
                if (def.passive && Math.random() > 0.997) this.ai.walkDir = -this.ai.walkDir;
                if (this.ai.stateTimer > 800 + Math.random() * 1500) { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            case 'idle_look':
                this.vx *= 0.85; this.right = Math.floor(this.ai.stateTimer / 500) % 2 === 0;
                if (this.ai.stateTimer > 1200) { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; } break;
            // === HOSTILE GROUND IDLE ===
            case 'idle_patrol': {
                const patSpd = def.spd * (0.3 + this.ai.restlessness * 0.25);
                this.ai.idleActivity += dt;
                if (this.ai.idleActivity > 1200 && this.ai.idleActivity < 1500) { this.vx *= 0.8; }
                else { this.vx = this.ai.walkDir * patSpd; if (this.ai.idleActivity > 1500) this.ai.idleActivity = 0; }
                this.right = this.ai.walkDir > 0;
                if (this._wallAhead(world, this.ai.walkDir) || this._gapAhead(world, this.ai.walkDir)) { this.ai.walkDir = -this.ai.walkDir; this.right = this.ai.walkDir > 0; }
                const hDist = this.ai.homeX - this.x;
                if (Math.abs(hDist) > this.ai.patrolDist * TS) { this.ai.walkDir = hDist > 0 ? 1 : -1; this.right = this.ai.walkDir > 0; }
                if (this.ground && this.ai.jumpCd <= 0 && this._wallAhead(world, this.ai.walkDir)) { this.vy = def.jumpH * 0.7; this.ground = false; this.ai.jumpCd = 600; this.squash = 0.85; this.stretch = 1.15; }
                if (this.ai.stateTimer > 3000 + Math.random() * 3000) { this.ai.state = Math.random() > 0.6 ? 'idle_sniff' : 'idle_stand'; this.ai.stateTimer = 0; this.ai.idleInterval = 600 + Math.random() * 1200; } break;
            }
            case 'idle_sniff': {
                this.vx *= 0.88;
                this.ai.headBob = Math.sin(this.ai.stateTimer / 120) * 2;
                const sniffP = Math.floor(this.ai.stateTimer / 350) % 4;
                if (sniffP === 1) this.right = true; else if (sniffP === 3) this.right = false;
                if (this.ground && this.ai.stateTimer > 800 && Math.random() > 0.992) { this.vy = def.jumpH * 0.25; this.ground = false; this.squash = 0.9; this.stretch = 1.1; }
                if (this.ai.stateTimer > 1500 + Math.random() * 1200) { this.ai.state = Math.random() > 0.4 ? 'idle_patrol' : 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            case 'idle_scratch': {
                this.vx *= 0.85;
                const scrC = Math.sin(this.ai.stateTimer / 80);
                this.vx += this.ai.walkDir * scrC * 0.4;
                this.squash = 1 + scrC * 0.06; this.stretch = 1 - scrC * 0.06;
                if (this.ai.stateTimer > 1200 + Math.random() * 800) { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            case 'idle_rest': {
                this.vx *= 0.92;
                this.squash += (1.18 - this.squash) * 0.05; this.stretch += (0.85 - this.stretch) * 0.05;
                this.squash += Math.sin(this.ai.stateTimer / 800) * 0.008;
                if (this.ai.stateTimer > 4000 + Math.random() * 4000) { this.ai.state = 'idle_sniff'; this.ai.stateTimer = 0; this.squash = 0.8; this.stretch = 1.2; } break;
            }
            // === FLYING IDLE ===
            case 'idle_fly_circle': {
                this.ai.circleAngle += dt * 0.0018 * (this.ai.personality % 2 === 0 ? 1 : -1);
                const flyR = 25 + Math.sin(this.ai.stateTimer * 0.0008 + this.id) * 15;
                const ftx = this.ai.homeX + Math.cos(this.ai.circleAngle) * flyR;
                const fty = this.ai.homeY + Math.sin(this.ai.circleAngle) * flyR * 0.6 - 15;
                this.vx += (ftx - this.x) * 0.006; this.vy += (fty - this.y) * 0.006;
                this.vx *= 0.96; this.vy *= 0.96;
                this.right = this.vx > 0.2 ? true : (this.vx < -0.2 ? false : this.right);
                if (this.ai.stateTimer > 2500 + Math.random() * 2500) { this.ai.state = Math.random() > 0.5 ? 'idle_perch' : 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            case 'idle_perch': {
                if (!this.ground) { this.vy += GRAV * 0.6; this.vx *= 0.92; }
                else { this.vx *= 0.8; if (Math.floor(this.ai.stateTimer / 800) % 2 !== Math.floor((this.ai.stateTimer - dt) / 800) % 2) this.right = !this.right; }
                if (this.ai.stateTimer > 2000 + Math.random() * 2500) { this.ai.state = 'idle_fly_circle'; this.ai.stateTimer = 0; this.vy = -3 - Math.random() * 2; this.squash = 0.8; this.stretch = 1.2; } break;
            }
            case 'idle_swoop': {
                if (this.ai.stateTimer < 350) { this.vy += 0.18; this.vx = this.ai.walkDir * def.spd * 0.7; }
                else { this.vy -= 0.22; this.vx *= 0.97; }
                this.right = this.vx > 0;
                if (this.ai.stateTimer > 700 + Math.random() * 300) { this.ai.state = 'idle_fly_circle'; this.ai.stateTimer = 0; } break;
            }
            // === PASSIVE IDLE ===
            case 'idle_graze': {
                this.vx *= 0.88;
                this.ai.headBob = Math.abs(Math.sin(this.ai.stateTimer / 180)) * 2.5;
                this.squash = 1 + Math.sin(this.ai.stateTimer / 180) * 0.04; this.stretch = 1 - Math.sin(this.ai.stateTimer / 180) * 0.04;
                if (Math.random() > 0.993) this.vx = (Math.random() - 0.5) * 0.6;
                if (this.ai.stateTimer > 2500 + Math.random() * 2000) { this.ai.state = Math.random() > 0.4 ? 'idle_walk' : 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            case 'idle_hop': {
                this.ai.idleActivity += dt;
                if (this.ground && this.ai.stateTimer > 250 + Math.random() * 200) {
                    this.vy = def.jumpH * (0.35 + Math.random() * 0.3); this.ground = false;
                    this.vx = this.ai.walkDir * def.spd * (0.4 + Math.random() * 0.3);
                    this.squash = 0.75; this.stretch = 1.25; this.ai.stateTimer = 0;
                    if (Math.random() > 0.55) this.ai.walkDir = -this.ai.walkDir;
                }
                if (Math.abs(this.vx) > 0.2) this.right = this.vx > 0;
                if (this._wallAhead(world, this.ai.walkDir) || this._gapAhead(world, this.ai.walkDir)) this.ai.walkDir = -this.ai.walkDir;
                if (this.ai.idleActivity > 3500 + Math.random() * 2500) { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; this.ai.idleActivity = 0; } break;
            }
            case 'idle_groom': {
                this.vx *= 0.88;
                this.squash = 1 + Math.sin(this.ai.stateTimer / 100) * 0.05; this.stretch = 1 - Math.sin(this.ai.stateTimer / 100) * 0.05;
                if (this.ai.stateTimer > 1500 + Math.random() * 1200) { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            // === VILLAGER IDLE ===
            case 'v_walk': {
                // Profession affects walk speed and style
                const vWalkSpd = this.ai.profession === 2 ? 1.0 : 0.75; // guards walk faster
                this.vx = this.ai.walkDir * def.spd * vWalkSpd; this.right = this.ai.walkDir > 0;
                if (this._wallAhead(world, this.ai.walkDir) || this._gapAhead(world, this.ai.walkDir)) { this.ai.walkDir = -this.ai.walkDir; this.right = this.ai.walkDir > 0; }
                const vhd = this.ai.homeX - this.x;
                if (Math.abs(vhd) > 200) { this.ai.walkDir = vhd > 0 ? 1 : -1; this.right = this.ai.walkDir > 0; }
                // Occasionally stop to look around
                if (Math.random() > 0.997) { this.vx *= 0.1; this.right = !this.right; }
                // Find other villagers to chat with
                const g2 = (typeof window._gameRef !== 'undefined') ? window._gameRef : null;
                if (g2 && Math.random() > 0.995) {
                    for (const m of g2.mobs) {
                        if (m !== this && m.def && m.def.isVillager && !m.dead) {
                            const cd = Math.abs(m.x - this.x);
                            if (cd < 30 && cd > 5) {
                                this.ai.state = 'v_chat'; this.ai.stateTimer = 0;
                                this.right = m.x > this.x;
                                break;
                            }
                        }
                    }
                }
                if (this.ai.stateTimer > 1200 + Math.random() * 2000) {
                    const vr = Math.random();
                    // Profession influences next activity
                    if (this.ai.profession === 0 && vr > 0.5) this.ai.state = 'v_work'; // blacksmith works more
                    else if (this.ai.profession === 1 && vr > 0.5) this.ai.state = 'v_tend'; // farmer tends crops
                    else if (this.ai.profession === 2 && vr > 0.4) this.ai.state = 'v_patrol'; // guard patrols
                    else if (vr > 0.6) this.ai.state = 'v_work';
                    else if (vr > 0.3) this.ai.state = 'v_inspect';
                    else this.ai.state = 'idle_stand';
                    this.ai.stateTimer = 0;
                }
                break;
            }
            case 'v_work': {
                this.vx *= 0.82;
                // Different work animations by profession
                const workFreq = this.ai.profession === 0 ? 100 : (this.ai.profession === 5 ? 160 : 140);
                this.squash = 1 + Math.sin(this.ai.stateTimer / workFreq) * 0.08; this.stretch = 1 - Math.sin(this.ai.stateTimer / workFreq) * 0.08;
                if (Math.random() > 0.994) this.right = !this.right;
                // Occasionally bob head (examining work)
                this.ai.headBob = Math.sin(this.ai.stateTimer / 300) * 1.5;
                if (this.ai.stateTimer > 3000 + Math.random() * 3500) { this.ai.state = Math.random() > 0.5 ? 'v_walk' : 'idle_stand'; this.ai.stateTimer = 0; this.ai.idleInterval = 200 + Math.random() * 500; } break;
            }
            case 'v_inspect': {
                this.vx *= 0.82;
                // Look around more naturally
                const insP = Math.floor(this.ai.stateTimer / 450) % 4;
                if (insP === 1) this.right = true;
                else if (insP === 3) this.right = false;
                // Crouch slightly to inspect
                this.squash = 1 + Math.sin(this.ai.stateTimer / 800) * 0.03;
                if (this.ai.stateTimer > 600 && Math.random() > 0.992) this.vx = this.ai.walkDir * 0.3;
                if (this.ai.stateTimer > 1800 + Math.random() * 1500) { this.ai.state = Math.random() > 0.5 ? 'v_walk' : 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            case 'v_chat': {
                this.vx *= 0.85;
                // Animated chatting — face partner, gesture
                if (Math.floor(this.ai.stateTimer / 800) % 2 !== Math.floor((this.ai.stateTimer - dt) / 800) % 2) {
                    // Nod/gesture animation
                    this.squash = 0.95; this.stretch = 1.05;
                }
                this.squash += (1 - this.squash) * 0.1;
                this.stretch += (1 - this.stretch) * 0.1;
                // Subtle body language
                this.ai.headBob = Math.sin(this.ai.stateTimer / 200) * 0.8;
                if (this.ai.stateTimer > 3000 + Math.random() * 2500) { this.ai.state = Math.random() > 0.4 ? 'v_walk' : 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            case 'v_sweep': {
                const swD = Math.sin(this.ai.stateTimer / 400);
                this.vx = swD * 0.5; this.right = swD > 0;
                this.squash = 1 + Math.sin(this.ai.stateTimer / 120) * 0.05; this.stretch = 1 - Math.sin(this.ai.stateTimer / 120) * 0.05;
                // Occasional pause
                if (Math.floor(this.ai.stateTimer / 1500) % 3 === 2) this.vx *= 0.2;
                if (this.ai.stateTimer > 2500 + Math.random() * 2000) { this.ai.state = Math.random() > 0.5 ? 'v_walk' : 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            case 'v_tend': {
                // Farmer tending crops — walk slowly, stop, crouch
                const tendPhase = Math.floor(this.ai.stateTimer / 1000) % 3;
                if (tendPhase === 0) { this.vx = this.ai.walkDir * def.spd * 0.3; this.right = this.ai.walkDir > 0; }
                else if (tendPhase === 1) { this.vx *= 0.8; this.squash = 1.1; this.stretch = 0.9; this.ai.headBob = Math.abs(Math.sin(this.ai.stateTimer / 180)) * 2; }
                else { this.vx *= 0.9; this.squash += (1 - this.squash) * 0.1; }
                if (this._wallAhead(world, this.ai.walkDir)) this.ai.walkDir = -this.ai.walkDir;
                if (this.ai.stateTimer > 4000 + Math.random() * 3000) { this.ai.state = Math.random() > 0.5 ? 'v_walk' : 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            case 'v_patrol': {
                // Guard patrol — walk at steady pace, look around periodically
                this.vx = this.ai.walkDir * def.spd * 0.9; this.right = this.ai.walkDir > 0;
                if (this._wallAhead(world, this.ai.walkDir) || this._gapAhead(world, this.ai.walkDir)) { this.ai.walkDir = -this.ai.walkDir; this.right = this.ai.walkDir > 0; }
                const patHome = this.ai.homeX - this.x;
                if (Math.abs(patHome) > 150) { this.ai.walkDir = patHome > 0 ? 1 : -1; this.right = this.ai.walkDir > 0; }
                // Stop and look around periodically
                if (Math.floor(this.ai.stateTimer / 2000) % 3 === 2) {
                    this.vx *= 0.2;
                    this.right = Math.floor(this.ai.stateTimer / 400) % 2 === 0;
                }
                if (this.ai.stateTimer > 5000 + Math.random() * 4000) { this.ai.state = Math.random() > 0.6 ? 'v_walk' : 'idle_stand'; this.ai.stateTimer = 0; } break;
            }
            case 'v_sit': {
                this.vx *= 0.92;
                this.squash += (1.12 - this.squash) * 0.04; this.stretch += (0.88 - this.stretch) * 0.04;
                this.squash += Math.sin(this.ai.stateTimer / 1000) * 0.005;
                if (this.ai.stateTimer > 5000 + Math.random() * 4000) { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; this.squash = 0.85; this.stretch = 1.15; this.ai.idleInterval = 200 + Math.random() * 400; } break;
            }
            case 'v_flee': {
                // Run away from hostile mob
                if (this.ai.fleeFromMob && !this.ai.fleeFromMob.dead) {
                    const fdx = this.x - this.ai.fleeFromMob.x;
                    this.vx = (fdx > 0 ? 1 : -1) * def.spd * 1.5;
                    this.right = this.vx > 0;
                    // Jump over obstacles
                    if (this.ground && this.ai.jumpCd <= 0 && this._wallAhead(world, fdx > 0 ? 1 : -1)) {
                        this.vy = def.jumpH; this.ground = false; this.ai.jumpCd = 400;
                    }
                } else {
                    this.ai.fleeFromMob = false;
                    this.ai.state = 'idle_stand'; this.ai.stateTimer = 0;
                }
                if (this.ai.stateTimer > 4000) { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; this.ai.fleeFromMob = false; }
                break;
            }
            case 'v_gohome': {
                // Walk toward home position at night
                const ghd = this.ai.homeX - this.x;
                if (Math.abs(ghd) > 20) {
                    this.vx = (ghd > 0 ? 1 : -1) * def.spd * 0.8;
                    this.right = this.vx > 0;
                    if (this._wallAhead(world, ghd > 0 ? 1 : -1) && this.ground && this.ai.jumpCd <= 0) {
                        this.vy = def.jumpH * 0.8; this.ground = false; this.ai.jumpCd = 500;
                    }
                } else {
                    this.ai.state = 'v_sleep'; this.ai.stateTimer = 0;
                }
                if (this.ai.stateTimer > 15000) { this.ai.state = 'v_sleep'; this.ai.stateTimer = 0; }
                break;
            }
            case 'v_sleep': {
                // Stay still, crouched, until morning
                this.vx *= 0.9;
                this.squash += (1.2 - this.squash) * 0.03; this.stretch += (0.82 - this.stretch) * 0.03;
                this.squash += Math.sin(this.ai.stateTimer / 1200) * 0.008;
                break;
            }
            case 'g_chase': {
                // Guard chases hostile mob
                if (this.ai._guardTarget && !this.ai._guardTarget.dead) {
                    const gt = this.ai._guardTarget;
                    const gdx = gt.x - this.x;
                    this.vx = (gdx > 0 ? 1 : -1) * def.spd * 1.2;
                    this.right = gdx > 0;
                    if (this.ground && this.ai.jumpCd <= 0) {
                        const ahead = Math.floor((this.x + this.w / 2 + (gdx > 0 ? 1 : -1) * TS) / TS);
                        const mty = Math.floor((this.y + this.h / 2) / TS);
                        if (SOLID.has(world.gt(ahead, mty))) {
                            this.vy = def.jumpH; this.ground = false; this.ai.jumpCd = 400;
                        }
                    }
                } else {
                    this.ai.state = 'idle_stand'; this.ai.stateTimer = 0;
                }
                // Return home if too far
                const gHomeDist = Math.abs(this.x - this.ai.homeX);
                if (gHomeDist > 300) { this.ai.state = 'v_gohome'; this.ai.stateTimer = 0; this.ai._guardTarget = null; }
                break;
            }
            case 'chase': {
                if (def.ground) {
                    // Predict player position for smarter pursuit
                    const predX = isSmart ? px2 + this.ai.playerVx * 10 : px2;
                    const pdx = predX - (this.x + this.w / 2);
                    const moveDir = pdx > 0 ? 1 : -1;
                    // Speed varies — faster when far, slower when close for menacing approach
                    const distFactor = Math.min(1, dist / 8);
                    const chaseSpd = def.spd * (0.7 + distFactor * 0.3);
                    this.vx = moveDir * chaseSpd;
                    if (Math.abs(pdx) > 6) this.right = pdx > 0;

                    // Smart jumping: pre-jump over gaps
                    if (this.ground && this.ai.jumpCd <= 0) {
                        const ahead = Math.floor((this.x + this.w / 2 + moveDir * TS) / TS);
                        const mty = Math.floor((this.y + this.h / 2) / TS);
                        // Wall ahead — jump
                        if (SOLID.has(world.gt(ahead, mty))) {
                            this.vy = def.jumpH; this.ground = false; this.ai.jumpCd = 350;
                            this.squash = 0.8; this.stretch = 1.2;
                        }
                        // Gap ahead — pre-jump over it
                        else if (this._gapAhead(world, moveDir) && dist < 12) {
                            this.vy = def.jumpH * 0.9; this.ground = false; this.ai.jumpCd = 500;
                            this.squash = 0.85; this.stretch = 1.15;
                        }
                        // Player is above — jump to reach
                        else if (dy < -TS * 2 && Math.abs(dx) < TS * 4) {
                            this.vy = def.jumpH; this.ground = false; this.ai.jumpCd = 400;
                        }
                    }
                } else {
                    // Flying mob AI — aggressive direct pursuit
                    this.vx += (dx > 0 ? 0.15 : -0.15) * def.spd;
                    this.vy += (dy > 0 ? 0.15 : -0.15) * def.spd;
                    this.vx = Math.max(-def.spd * 2, Math.min(def.spd * 2, this.vx));
                    this.vy = Math.max(-def.spd * 2, Math.min(def.spd * 2, this.vy));
                    if (Math.abs(dx) > 8) this.right = dx > 0;
                }
                break;
            }
            case 'leap_attack': {
                // Wind-up then leap!
                if (this.ai.stateTimer < 200) {
                    // Wind-up: crouch
                    this.vx *= 0.5;
                    this.squash = 1.3; this.stretch = 0.7;
                    this.right = dx > 0;
                } else if (this.ai.stateTimer < 250) {
                    // Launch!
                    const predX = px2 + this.ai.playerVx * 6;
                    const predY = py2 + this.ai.playerVy * 4;
                    const ldx = predX - (this.x + this.w / 2), ldy = predY - (this.y + this.h / 2);
                    const ld = Math.sqrt(ldx * ldx + ldy * ldy) || 1;
                    const leapSpd = def.spd * 3;
                    this.vx = (ldx / ld) * leapSpd;
                    this.vy = Math.min(-5, (ldy / ld) * leapSpd - 3);
                    this.ground = false;
                    this.squash = 0.6; this.stretch = 1.5;
                    this.ai.leapCd = 2000 + Math.random() * 1500;
                } else {
                    // In air after leap, wait for landing
                    if (this.ground || this.ai.stateTimer > 1000) {
                        this.ai.state = 'chase'; this.ai.stateTimer = 0;
                    }
                }
                break;
            }
            case 'circle': {
                // Flying mobs circle the player
                this.ai.circleAngle += dt * 0.003 * (this.ai.strafeDir);
                const circR = 60 + Math.sin(this.ai.stateTimer * 0.002) * 20;
                const tgtX = px2 + Math.cos(this.ai.circleAngle) * circR;
                const tgtY = py2 + Math.sin(this.ai.circleAngle) * circR - 30;
                this.vx += (tgtX - this.x) * 0.008; this.vy += (tgtY - this.y) * 0.008;
                this.vx *= 0.95; this.vy *= 0.95;
                this.right = dx > 0;
                // Dash in to attack periodically
                if (this.ai.stateTimer > 1500 + Math.random() * 1000) {
                    this.ai.state = 'chase'; this.ai.stateTimer = 0;
                }
                break;
            }
            case 'retreat': {
                // Run away briefly then re-engage
                const fleeDir = dx > 0 ? -1 : 1;
                if (def.ground) {
                    this.vx = fleeDir * def.spd * 1.2;
                    if (this.ground && this.ai.jumpCd <= 0 && (this._wallAhead(world, fleeDir) || Math.random() > 0.95)) {
                        this.vy = def.jumpH; this.ground = false; this.ai.jumpCd = 500;
                    }
                } else {
                    this.vx += fleeDir * 0.15 * def.spd;
                    this.vy += (dy > 0 ? -0.1 : 0.1) * def.spd;
                    this.vx = Math.max(-def.spd * 2, Math.min(def.spd * 2, this.vx));
                    this.vy = Math.max(-def.spd * 2, Math.min(def.spd * 2, this.vy));
                }
                this.right = this.vx > 0;
                this.ai.retreatTimer -= dt;
                if (this.ai.retreatTimer <= 0 || dist > 12) {
                    this.ai.state = 'chase'; this.ai.stateTimer = 0;
                }
                break;
            }
            case 'flee':
                this.vx = dx > 0 ? -def.spd : def.spd;
                if (Math.abs(this.vx) > 0.3) this.right = this.vx > 0;
                // Smarter flee: avoid running into walls
                const flDir = dx > 0 ? -1 : 1;
                if (this._wallAhead(world, flDir) && this.ground && this.ai.jumpCd <= 0) {
                    this.vy = def.jumpH; this.ground = false; this.ai.jumpCd = 400;
                } else if (this.ground && this.ai.jumpCd <= 0 && Math.random() > 0.92) {
                    this.vy = def.jumpH; this.ground = false; this.ai.jumpCd = 600;
                }
                break;
        }

        // Chronolisk teleport — blinks short distances when chasing
        if (this.type === 'CHRONOLISK' && this.ai.state === 'chase' && dist < 15 && dist > 4 && Math.random() > 0.992) {
            const teleDir = dx > 0 ? 1 : -1;
            const teleX = player.x + PW / 2 + teleDir * (TS * 2 + Math.random() * TS * 2);
            const teleY = this.y;
            // Only teleport if destination is valid
            const ttx = Math.floor(teleX / TS), tty = Math.floor(teleY / TS);
            if (!SOLID.has(world.gt(ttx, tty)) && !SOLID.has(world.gt(ttx, tty - 1))) {
                this.x = teleX; this.vx = -teleDir * 3;
                this.squash = 0.5; this.stretch = 1.6;
            }
        }
        if (def.ground) this.vy += GRAV; this.vy = Math.max(-12, Math.min(12, this.vy));

        // === LIQUID INTERACTION for mobs ===
        const mobCX = Math.floor((this.x + this.w / 2) / TS);
        const mobCY = Math.floor((this.y + this.h / 2) / TS);
        const mobFeetY = Math.floor((this.y + this.h - 1) / TS);
        const tileAtMob = world.gt(mobCX, mobCY);
        const tileAtFeet = world.gt(mobCX, mobFeetY);
        const mobInLiquid = LIQUID.has(tileAtMob) || LIQUID.has(tileAtFeet);
        const mobLiquidType = LIQUID.has(tileAtMob) ? tileAtMob : (LIQUID.has(tileAtFeet) ? tileAtFeet : 0);

        if (mobInLiquid) {
            // Slow down movement in liquid
            const liqDrag = mobLiquidType === T.HONEY ? 0.3 : (mobLiquidType === T.LAVA ? 0.45 : 0.55);
            this.vx *= (0.7 + liqDrag * 0.3);
            // Reduce gravity in liquid (buoyancy)
            if (def.ground) {
                this.vy -= GRAV * 0.75; // counteract most gravity
                this.vy *= 0.88; // dampen vertical
            } else {
                this.vy *= 0.9;
            }
            // Lava damage to mobs (not fire-immune types)
            if (mobLiquidType === T.LAVA) {
                const fireImmune = ['DEMON', 'FIRE_ELEM', 'MAGMA_SLIME', 'LAVA_SERPENT', 'IMP', 'ASH_WRAITH'].includes(this.type);
                if (!fireImmune) {
                    if (!this._lavaDmgTimer) this._lavaDmgTimer = 0;
                    this._lavaDmgTimer += 16; // approximate dt
                    if (this._lavaDmgTimer >= 500) {
                        this._lavaDmgTimer = 0;
                        this.hp -= 8;
                        this.hitTimer = 200;
                        if (this.hp <= 0) this.dead = true;
                    }
                }
            }
        }

        this.ground = false;
        let nx = this.x + this.vx; if (!this.collides(world, nx, this.y)) this.x = nx; else {
            this.vx = 0;
            if (this.ai.state === 'idle_walk') { this.ai.state = 'idle_stand'; this.ai.stateTimer = 0; }
        }
        let ny = this.y + this.vy; if (!this.collides(world, this.x, ny)) this.y = ny; else { if (this.vy > 0) this.ground = true; this.vy = 0; }
        this.x = Math.max(0, Math.min((WW - 2) * TS, this.x)); this.y = Math.max(0, Math.min((WH - 2) * TS, this.y));

        // Worm segment update — with sine wave for more organic movement
        if (this.segments) {
            const hx = this.x + this.w / 2, hy = this.y + this.h / 2;
            let ppx = hx, ppy = hy;
            for (let i = 0; i < this.segments.length; i++) {
                const s = this.segments[i], segDist = 12;
                // Add sine wave offset for organic wriggle
                const waveX = Math.sin(performance.now() / 200 + i * 0.8) * 3;
                const waveY = Math.cos(performance.now() / 250 + i * 0.6) * 2;
                const targetX = ppx + waveX * (i / this.segments.length);
                const targetY = ppy + waveY * (i / this.segments.length);
                const sdx = s.x - targetX, sdy = s.y - targetY;
                const sd = Math.sqrt(sdx * sdx + sdy * sdy);
                if (sd > segDist) {
                    s.x = targetX + (sdx / sd) * segDist;
                    s.y = targetY + (sdy / sd) * segDist;
                } else {
                    s.x += (targetX - s.x) * 0.1;
                    s.y += (targetY - s.y) * 0.1;
                }
                ppx = s.x; ppy = s.y;
            }
        }

        if (!def.passive && def.dmg > 0 && this.ai.attackCd <= 0 && this.x + this.w > player.x && this.x < player.x + PW && this.y + this.h > player.y && this.y < player.y + PH) {
            // Leap attacks do extra damage
            const leapBonus = this.ai.state === 'leap_attack' ? 1.5 : 1;
            const dmg = Math.ceil(def.dmg * leapBonus);
            const g = (typeof window._gameRef !== 'undefined') ? window._gameRef : null;
            const armorRed = g ? ARMOR_TIERS[g.equipArmor].def : 0;
            let finalDmg = Math.max(1, Math.ceil(dmg * (1 - armorRed)));
            if (g && g._buffs && g._buffs.shield) finalDmg = Math.ceil(finalDmg / 2);
            player.hp = Math.max(0, player.hp - finalDmg); this.ai.attackCd = 1000;
            const kbStr = leapBonus > 1 ? 6 : 4;
            player.vx += (player.x - this.x > 0 ? kbStr : -kbStr); player.vy -= 3; player.dmgFlash = 200; sfx.hurt();
        }
    }
    collides(w, x, y) {
        const l = Math.floor(x / TS), r = Math.floor((x + this.w - 1) / TS), t = Math.floor(y / TS), b = Math.floor((y + this.h - 1) / TS);
        for (let tx = l; tx <= r; tx++)for (let ty = t; ty <= b; ty++)if (SOLID.has(w.gt(tx, ty))) return true; return false;
    }
    render(ctx, time) { if (!this.dead) MobRenderer.draw(ctx, this, time); }
}

// === EYE OF CTHULHU BOSS ===
export class EyeOfCthulhu {
    constructor(px, py) {
        this.x = px; this.y = py - 180;
        this.w = 48; this.h = 42;
        this.vx = 0; this.vy = 0;
        this.hp = 800; this.maxHp = 800;
        this.phase = 1; this.alive = true;
        this.angle = 0; this.targetAngle = 0;
        this.state = 'spawn'; this.stateTimer = 0;
        this.dashDir = { x: 0, y: 0 };
        this.dashCount = 0; this.maxDash = 3;
        this.hitTimer = 0; this.iframes = 0;
        this.spawnTimer = 0; this.phaseTransition = 0;
        this.pupilOff = { x: 0, y: 0 };
        this.mouthOpen = 0;
        this.trailParts = [];
        this.minions = [];
        this.enraged = false;
        this.deathTimer = 0;
        this.squash = 1; this.stretch = 1;
        this.glowPulse = 0;
        sfx.bossRoar();
    }

    update(dt, player, game) {
        if (!this.alive) return;
        this.stateTimer += dt;
        this.hitTimer = Math.max(0, this.hitTimer - dt);
        this.iframes = Math.max(0, this.iframes - dt);
        this.spawnTimer += dt;
        this.glowPulse += dt * 0.005;
        this.squash += (1 - this.squash) * 0.12;
        this.stretch += (1 - this.stretch) * 0.12;

        const dx = (player.x + PW / 2) - (this.x + this.w / 2);
        const dy = (player.y + PH / 2) - (this.y + this.h / 2);
        this.targetAngle = Math.atan2(dy, dx);
        this.angle += (this.targetAngle - this.angle) * 0.08;
        this.pupilOff.x += (Math.cos(this.targetAngle) * 4 - this.pupilOff.x) * 0.1;
        this.pupilOff.y += (Math.sin(this.targetAngle) * 4 - this.pupilOff.y) * 0.1;

        // Trail particles
        if (this.spawnTimer > 500 && Math.random() > 0.5) {
            this.trailParts.push({ x: this.x + this.w / 2 + (Math.random() - 0.5) * 20, y: this.y + this.h / 2 + (Math.random() - 0.5) * 20, vx: (Math.random() - 0.5) * 1.5, vy: (Math.random() - 0.5) * 1.5, life: 40, c: this.phase === 2 ? '#ff2244' : '#882244' });
        }
        for (let i = this.trailParts.length - 1; i >= 0; i--) { const p = this.trailParts[i]; p.x += p.vx; p.y += p.vy; p.life--; if (p.life <= 0) this.trailParts.splice(i, 1); }

        // Phase check
        if (this.phase === 1 && this.hp < this.maxHp * 0.4 && this.phaseTransition === 0) {
            this.phaseTransition = 1; this.state = 'phase_change'; this.stateTimer = 0;
            sfx.bossPhaseChange(); game.addShake(8, 400);
        }

        // Death
        if (this.hp <= 0 && this.deathTimer === 0) {
            this.deathTimer = 1; this.state = 'dying'; this.stateTimer = 0;
            sfx.bossDeath(); game.addShake(12, 600);
        }
        if (this.state === 'dying') {
            this.deathTimer += dt;
            if (this.deathTimer > 2000) { this.alive = false; }
            for (let i = 0; i < 3; i++) game.addPart(this.x + Math.random() * this.w, this.y + Math.random() * this.h, (Math.random() - 0.5) * 6, (Math.random() - 0.5) * 6, ['#ff2244', '#882244', '#ff6644', '#ffaa44'][Math.floor(Math.random() * 4)], 30 + Math.random() * 20);
            if (Math.random() > 0.7) game.addShake(3, 80);
            this.squash = 1 + Math.sin(this.deathTimer * 0.02) * 0.2;
            this.stretch = 1 - Math.sin(this.deathTimer * 0.02) * 0.2;
            return;
        }

        // State machine
        switch (this.state) {
            case 'spawn':
                this.vy = -0.5; this.vx = 0;
                if (this.stateTimer > 1500) { this.state = 'hover'; this.stateTimer = 0; }
                break;
            case 'hover': {
                const hoverY = player.y - 120 + Math.sin(this.spawnTimer * 0.002) * 30;
                const hoverX = player.x + Math.sin(this.spawnTimer * 0.0015) * 100;
                this.vx += (hoverX - this.x) * 0.003;
                this.vy += (hoverY - this.y) * 0.003;
                this.vx *= 0.95; this.vy *= 0.95;
                const interval = this.phase === 2 ? 1500 : 2500;
                if (this.stateTimer > interval) {
                    this.state = 'pre_dash'; this.stateTimer = 0;
                    this.dashCount = 0;
                    this.maxDash = this.phase === 2 ? (this.enraged ? 5 : 4) : 3;
                }
                break;
            }
            case 'pre_dash': {
                this.vx *= 0.9; this.vy *= 0.9;
                if (this.stateTimer > (this.phase === 2 ? 300 : 500)) {
                    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
                    const spd = this.phase === 2 ? (this.enraged ? 10 : 8) : 6;
                    this.dashDir = { x: dx / dist * spd, y: dy / dist * spd };
                    this.vx = this.dashDir.x; this.vy = this.dashDir.y;
                    this.state = 'dashing'; this.stateTimer = 0;
                    this.squash = 0.7; this.stretch = 1.4;
                    sfx.bossDash(); game.addShake(3 + this.phase, 80);
                }
                break;
            }
            case 'dashing':
                if (this.stateTimer > (this.phase === 2 ? 350 : 450)) {
                    this.dashCount++;
                    if (this.dashCount >= this.maxDash) {
                        this.state = this.phase === 2 && Math.random() > 0.5 ? 'summon' : 'hover';
                    } else {
                        this.state = 'pre_dash';
                    }
                    this.stateTimer = 0; this.vx *= 0.3; this.vy *= 0.3;
                }
                break;
            case 'summon':
                this.vx *= 0.92; this.vy *= 0.92;
                if (this.stateTimer > 800) {
                    for (let i = 0; i < 3; i++) {
                        const a = Math.PI * 2 / 3 * i + this.spawnTimer * 0.001;
                        this.minions.push({ x: this.x + this.w / 2 + Math.cos(a) * 30, y: this.y + this.h / 2 + Math.sin(a) * 30, vx: 0, vy: 0, hp: 20, maxHp: 20, w: 12, h: 10, hitTimer: 0 });
                    }
                    game.addShake(2, 60);
                    this.state = 'hover'; this.stateTimer = 0;
                }
                break;
            case 'phase_change':
                this.vx *= 0.9; this.vy *= 0.9;
                this.mouthOpen += (1 - this.mouthOpen) * 0.03;
                if (this.stateTimer > 2000) {
                    this.phase = 2; this.phaseTransition = 2;
                    this.state = 'hover'; this.stateTimer = 0;
                    this.enraged = this.hp < this.maxHp * 0.15;
                    sfx.bossRoar(); game.addShake(6, 300);
                    for (let i = 0; i < 20; i++) game.addPart(this.x + this.w / 2, this.y + this.h / 2, (Math.random() - 0.5) * 8, (Math.random() - 0.5) * 8, '#ff2244', 40);
                }
                break;
        }

        this.x += this.vx; this.y += this.vy;
        if (this.phase === 2) this.mouthOpen += (0.6 + Math.sin(this.spawnTimer * 0.008) * 0.3 - this.mouthOpen) * 0.05;
        if (this.phase === 2 && this.hp < this.maxHp * 0.15 && !this.enraged) { this.enraged = true; sfx.bossRoar(); game.addShake(6, 300); }

        // Minion update
        for (let i = this.minions.length - 1; i >= 0; i--) {
            const m = this.minions[i];
            m.hitTimer = Math.max(0, m.hitTimer - dt);
            const mdx = (player.x + PW / 2) - m.x, mdy = (player.y + PH / 2) - m.y;
            const md = Math.sqrt(mdx * mdx + mdy * mdy) || 1;
            m.vx += mdx / md * 0.15; m.vy += mdy / md * 0.15;
            m.vx *= 0.96; m.vy *= 0.96;
            m.x += m.vx; m.y += m.vy;
            if (m.x + m.w > player.x && m.x < player.x + PW && m.y + m.h > player.y && m.y < player.y + PH && player.iframes <= 0) {
                player.hp = Math.max(0, player.hp - 5); player.iframes = 500; player.dmgFlash = 200;
                player.vx += mdx < 0 ? 3 : -3; player.vy -= 2; sfx.hurt();
            }
            if (m.hp <= 0) { this.minions.splice(i, 1); for (let j = 0; j < 5; j++) game.addPart(m.x, m.y, (Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4, '#ff4466', 25); }
        }

        // Player collision
        if (this.x + this.w > player.x && this.x < player.x + PW && this.y + this.h > player.y && this.y < player.y + PH && player.iframes <= 0 && this.state !== 'dying') {
            const dmg = this.phase === 2 ? (this.enraged ? 25 : 18) : 12;
            player.hp = Math.max(0, player.hp - dmg); player.iframes = 600; player.dmgFlash = 250;
            const kb = this.state === 'dashing' ? 8 : 4;
            player.vx += dx < 0 ? kb : -kb; player.vy -= 4;
            sfx.hurt(); game.addShake(4, 100);
            game.dmgNums.add(player.x + PW / 2, player.y - 5, dmg, '#ff2244');
        }
    }

    takeDamage(dmg, game) {
        if (this.iframes > 0 || this.state === 'dying' || !this.alive) return false;
        this.hp = Math.max(0, this.hp - dmg);
        this.hitTimer = 250; this.iframes = 200;
        this.squash = 1.3; this.stretch = 0.75;
        sfx.bossHit(); game.addShake(2, 50);
        game.dmgNums.add(this.x + this.w / 2, this.y - 8, dmg, '#ffcc00');
        for (let i = 0; i < 4; i++) game.addPart(this.x + this.w / 2, this.y + this.h / 2, (Math.random() - 0.5) * 5, (Math.random() - 0.5) * 5, this.phase === 2 ? '#ff2244' : '#882244', 25);
        return true;
    }

    render(ctx, time) {
        if (!this.alive) return;
        const cx = this.x + this.w / 2, cy = this.y + this.h / 2;
        const flash = this.hitTimer > 0 && Math.floor(this.hitTimer / 40) % 2 === 0;
        const dying = this.state === 'dying';
        const dAlpha = dying ? Math.max(0, 1 - this.deathTimer / 2000) : 1;

        // Trail
        for (const p of this.trailParts) { ctx.globalAlpha = (p.life / 40) * 0.4 * dAlpha; ctx.fillStyle = p.c; ctx.fillRect(p.x - 2, p.y - 2, 4, 4); }
        ctx.globalAlpha = dAlpha;

        // Glow
        const glow = Math.sin(this.glowPulse) * 0.15 + 0.25;
        const gc = this.phase === 2 ? `rgba(255,30,60,${glow})` : `rgba(150,30,80,${glow})`;
        ctx.fillStyle = gc;
        ctx.beginPath(); ctx.arc(cx, cy, this.w * 0.9, 0, Math.PI * 2); ctx.fill();

        ctx.save(); ctx.translate(cx, cy); ctx.rotate(this.angle); ctx.scale(this.squash, this.stretch);

        // Eye body
        const bCol = flash ? '#fff' : (this.phase === 2 ? '#aa1133' : '#881144');
        ctx.fillStyle = bCol;
        ctx.beginPath(); ctx.ellipse(0, 0, this.w / 2, this.h / 2, 0, 0, Math.PI * 2); ctx.fill();

        // Veins
        ctx.strokeStyle = this.phase === 2 ? 'rgba(255,100,100,0.6)' : 'rgba(200,50,80,0.4)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 6; i++) {
            const a = i * Math.PI / 3 + Math.sin(time / 500 + i) * 0.2;
            ctx.beginPath(); ctx.moveTo(Math.cos(a) * 8, Math.sin(a) * 6);
            ctx.lineTo(Math.cos(a) * (this.w / 2 - 2), Math.sin(a) * (this.h / 2 - 2)); ctx.stroke();
        }

        // Sclera
        const irisR = this.phase === 2 ? 8 : 10;
        ctx.fillStyle = flash ? '#ffd' : '#e8ddd0';
        ctx.beginPath(); ctx.ellipse(0, 0, irisR + 4, irisR + 2, 0, 0, Math.PI * 2); ctx.fill();

        // Iris
        ctx.fillStyle = flash ? '#f88' : (this.phase === 2 ? '#ff1133' : '#cc2255');
        ctx.beginPath(); ctx.arc(this.pupilOff.x * 0.3, this.pupilOff.y * 0.3, irisR, 0, Math.PI * 2); ctx.fill();

        // Pupil
        const pSize = this.phase === 2 ? 3 : 4;
        ctx.fillStyle = '#111';
        ctx.beginPath(); ctx.ellipse(this.pupilOff.x * 0.4, this.pupilOff.y * 0.4, pSize, pSize + 1, 0, 0, Math.PI * 2); ctx.fill();

        // Highlight
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.beginPath(); ctx.arc(-3 + this.pupilOff.x * 0.2, -3 + this.pupilOff.y * 0.2, 2, 0, Math.PI * 2); ctx.fill();

        // Mouth (phase 2)
        if (this.mouthOpen > 0.05) {
            const mo = this.mouthOpen;
            ctx.fillStyle = '#440011';
            ctx.beginPath(); ctx.ellipse(this.w / 2 + 4, 0, 8 * mo, 12 * mo, 0, 0, Math.PI * 2); ctx.fill();
            // Teeth
            ctx.fillStyle = '#eeddcc';
            for (let i = -2; i <= 2; i++) {
                ctx.fillRect(this.w / 2 + 2, i * 4 * mo - 1, 5 * mo, 2);
            }
        }

        // Tentacles at back
        ctx.strokeStyle = this.phase === 2 ? '#cc1133' : '#771133';
        ctx.lineWidth = 2;
        for (let i = 0; i < 5; i++) {
            const ta = (i - 2) * 0.35 + Math.PI;
            const tLen = 14 + Math.sin(time / 200 + i * 1.5) * 5;
            ctx.beginPath();
            ctx.moveTo(Math.cos(ta) * (this.w / 2 - 4), Math.sin(ta) * (this.h / 2 - 3));
            ctx.lineTo(Math.cos(ta) * (this.w / 2 + tLen) + Math.sin(time / 150 + i) * 3, Math.sin(ta) * (this.h / 2 + tLen) + Math.cos(time / 180 + i) * 3);
            ctx.stroke();
        }

        ctx.restore();

        // Minions
        for (const m of this.minions) {
            const mf = m.hitTimer > 0 && Math.floor(m.hitTimer / 40) % 2 === 0;
            ctx.fillStyle = mf ? '#fff' : '#993344';
            ctx.beginPath(); ctx.ellipse(m.x + m.w / 2, m.y + m.h / 2, m.w / 2, m.h / 2, 0, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = '#eee'; ctx.beginPath(); ctx.arc(m.x + m.w / 2, m.y + m.h / 2, 3, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = '#222'; ctx.beginPath(); ctx.arc(m.x + m.w / 2 + 1, m.y + m.h / 2, 1.5, 0, Math.PI * 2); ctx.fill();
            if (m.hp < m.maxHp) { ctx.fillStyle = '#300'; ctx.fillRect(m.x, m.y - 4, m.w, 2); ctx.fillStyle = '#f44'; ctx.fillRect(m.x, m.y - 4, m.w * (m.hp / m.maxHp), 2); }
        }
        ctx.globalAlpha = 1;
    }
}

// === MOON LORD BOSS ===
export class MoonLord {
    constructor(px, py) {
        this.cx = px; this.cy = py - 200;
        this.alive = true; this.spawnT = 0; this.phase = 1;
        this.deathTimer = 0; this.state = 'spawn'; this.stateTimer = 0;
        this.hitTimer = 0; this.glowPulse = 0;
        // Parts: head, leftHand, rightHand, core
        const mkEye = (ox, oy, hp) => ({ ox, oy, x: 0, y: 0, hp, maxHp: hp, alive: true, open: 1, pupilOff: { x: 0, y: 0 }, hitTimer: 0, iframes: 0, angle: 0 });
        this.head = mkEye(0, -70, 400);
        this.lHand = mkEye(-90, 20, 300);
        this.rHand = mkEye(90, 20, 300);
        this.core = { hp: 500, maxHp: 500, alive: true, open: 0, hitTimer: 0, iframes: 0, pulse: 0 };
        this.coreVuln = false;
        this.projectiles = [];
        this.trailParts = [];
        this.laser = { active: false, angle: 0, timer: 0, chargeT: 0, src: { x: 0, y: 0 } };
        this.floatOff = 0;
        this.w = 60; this.h = 100;
        this.tint = 0;
        sfx.bossRoar();
    }
    get x() { return this.cx - this.w / 2; }
    get y() { return this.cy - this.h / 2 + Math.sin(this.floatOff) * 8; }
    get maxHp() {
        if (this.coreVuln) return this.core.maxHp;
        return this.head.maxHp + this.lHand.maxHp + this.rHand.maxHp;
    }
    get hp() {
        if (this.coreVuln) return this.core.hp;
        return Math.max(0, this.head.hp) + Math.max(0, this.lHand.hp) + Math.max(0, this.rHand.hp);
    }
    eyesDead() { return this.head.hp <= 0 && this.lHand.hp <= 0 && this.rHand.hp <= 0; }
    update(dt, player, game) {
        if (!this.alive) return;
        this.stateTimer += dt; this.spawnT += dt;
        this.hitTimer = Math.max(0, this.hitTimer - dt);
        this.floatOff += dt * 0.002;
        this.glowPulse += dt * 0.004;
        this.tint = Math.min(1, this.tint + dt * 0.0005);
        const px = player.x + PW / 2, py = player.y + PH / 2;
        // Float toward player
        const tdx = px - this.cx, tdy = py - 140 - this.cy;
        this.cx += tdx * 0.004; this.cy += tdy * 0.003;
        // Update parts positions
        const parts = [this.head, this.lHand, this.rHand];
        for (const p of parts) {
            p.x = this.cx + p.ox + Math.sin(this.spawnT * 0.001 + p.ox) * 5;
            p.y = this.cy + p.oy + Math.sin(this.floatOff + p.ox * 0.1) * 6;
            p.hitTimer = Math.max(0, p.hitTimer - dt);
            p.iframes = Math.max(0, p.iframes - dt);
            const edx = px - p.x, edy = py - p.y;
            p.angle = Math.atan2(edy, edx);
            p.pupilOff.x += (Math.cos(p.angle) * 3 - p.pupilOff.x) * 0.08;
            p.pupilOff.y += (Math.sin(p.angle) * 3 - p.pupilOff.y) * 0.08;
        }
        this.core.hitTimer = Math.max(0, this.core.hitTimer - dt);
        this.core.iframes = Math.max(0, this.core.iframes - dt);
        this.core.pulse += dt * 0.006;
        // Check if all eyes dead -> core vulnerable
        if (!this.coreVuln && this.eyesDead()) {
            this.coreVuln = true; this.core.open = 0;
            this.state = 'core_open'; this.stateTimer = 0;
            sfx.bossPhaseChange(); game.addShake(10, 500);
            for (let i = 0; i < 25; i++) game.addPart(this.cx, this.cy, (Math.random() - .5) * 8, (Math.random() - .5) * 8, '#44ffcc', 50);
        }
        // Death
        if (this.coreVuln && this.core.hp <= 0 && this.deathTimer === 0) {
            this.deathTimer = 1; this.state = 'dying'; this.stateTimer = 0;
            sfx.bossDeath(); game.addShake(15, 800);
        }
        if (this.state === 'dying') {
            this.deathTimer += dt;
            if (this.deathTimer > 3000) this.alive = false;
            for (let i = 0; i < 4; i++) game.addPart(this.cx + (Math.random() - .5) * 80, this.cy + (Math.random() - .5) * 120, (Math.random() - .5) * 7, (Math.random() - .5) * 7, ['#44ffcc', '#2288aa', '#66ffee', '#115566'][Math.floor(Math.random() * 4)], 40);
            if (Math.random() > .6) game.addShake(4, 100);
            this.tint = Math.max(0, this.tint - dt * 0.001);
            return;
        }
        // Trail
        if (this.spawnT > 800 && Math.random() > .6) {
            this.trailParts.push({ x: this.cx + (Math.random() - .5) * 70, y: this.cy + (Math.random() - .5) * 100, vx: (Math.random() - .5) * 1, vy: -Math.random() * 1.5, life: 50, c: this.coreVuln ? '#44ffcc' : '#2288aa' });
        }
        for (let i = this.trailParts.length - 1; i >= 0; i--) { const t = this.trailParts[i]; t.x += t.vx; t.y += t.vy; t.life--; if (t.life <= 0) this.trailParts.splice(i, 1); }
        // Attack patterns
        if (this.state === 'spawn') {
            if (this.stateTimer > 2000) { this.state = 'idle'; this.stateTimer = 0; }
        } else if (this.state === 'idle') {
            if (this.stateTimer > (this.coreVuln ? 1200 : 2000)) {
                const atks = this.coreVuln ? ['bolt_storm'] : ['phantom_bolt', 'homing_sphere', 'deathray'];
                this.state = atks[Math.floor(Math.random() * atks.length)];
                this.stateTimer = 0;
            }
        } else if (this.state === 'phantom_bolt') {
            if (this.stateTimer > 400) {
                for (const p of parts) {
                    if (p.hp <= 0) continue;
                    const a = p.angle + (Math.random() - .5) * .3;
                    this.projectiles.push({ x: p.x, y: p.y, vx: Math.cos(a) * 4, vy: Math.sin(a) * 4, life: 120, r: 3, c: '#44ddcc', dmg: 8, type: 'bolt' });
                }
                sfx.bossDash(); game.addShake(2, 40);
                this.state = 'idle'; this.stateTimer = 0;
            }
        } else if (this.state === 'homing_sphere') {
            if (this.stateTimer > 600) {
                const src = parts.filter(p => p.hp > 0);
                if (src.length > 0) {
                    const s = src[Math.floor(Math.random() * src.length)];
                    this.projectiles.push({ x: s.x, y: s.y, vx: 0, vy: 0, life: 200, r: 5, c: '#88ffee', dmg: 12, type: 'homing' });
                }
                sfx.bossDash();
                this.state = 'idle'; this.stateTimer = 0;
            }
        } else if (this.state === 'deathray') {
            if (this.head.hp > 0) {
                if (this.stateTimer < 800) {
                    this.laser.chargeT = this.stateTimer / 800;
                    this.laser.src = { x: this.head.x, y: this.head.y };
                    this.laser.angle = this.head.angle;
                } else if (this.stateTimer < 2000) {
                    this.laser.active = true;
                    this.laser.src = { x: this.head.x, y: this.head.y };
                    this.laser.angle += ((this.head.angle - this.laser.angle) * 0.02);
                    this.laser.timer = this.stateTimer - 800;
                    // Laser damage
                    const la = this.laser.angle;
                    for (let d = 0; d < 300; d += 8) {
                        const lx = this.laser.src.x + Math.cos(la) * d, ly = this.laser.src.y + Math.sin(la) * d;
                        if (Math.abs(lx - px) < 12 && Math.abs(ly - py) < 16 && player.iframes <= 0) {
                            player.hp = Math.max(0, player.hp - 2); player.iframes = 100; player.dmgFlash = 80;
                            player.vx += Math.cos(la) * 2; player.vy += Math.sin(la) * 2;
                            game.addShake(2, 30); break;
                        }
                    }
                    if (Math.random() > .7) game.addShake(1, 20);
                } else {
                    this.laser.active = false; this.laser.chargeT = 0;
                    this.state = 'idle'; this.stateTimer = 0;
                }
            } else { this.laser.active = false; this.state = 'idle'; this.stateTimer = 0; }
        } else if (this.state === 'bolt_storm') {
            if (this.stateTimer > 300 && this.stateTimer < 1500 && Math.floor(this.stateTimer / 200) !== Math.floor((this.stateTimer - dt) / 200)) {
                for (let i = 0; i < 3; i++) {
                    const a = Math.random() * Math.PI * 2;
                    this.projectiles.push({ x: this.cx + Math.cos(a) * 40, y: this.cy + Math.sin(a) * 40, vx: Math.cos(a) * 3.5, vy: Math.sin(a) * 3.5, life: 100, r: 3, c: '#66ffcc', dmg: 10, type: 'bolt' });
                }
                sfx.bossDash();
            }
            if (this.stateTimer > 1800) { this.state = 'idle'; this.stateTimer = 0; }
        } else if (this.state === 'core_open') {
            this.core.open = Math.min(1, this.core.open + dt * 0.001);
            if (this.stateTimer > 1500) { this.state = 'idle'; this.stateTimer = 0; }
        }
        // Update projectiles
        for (let i = this.projectiles.length - 1; i >= 0; i--) {
            const pr = this.projectiles[i]; pr.life--;
            if (pr.type === 'homing') {
                const hdx = px - pr.x, hdy = py - pr.y, hd = Math.sqrt(hdx * hdx + hdy * hdy) || 1;
                pr.vx += hdx / hd * 0.2; pr.vy += hdy / hd * 0.2;
                const spd = Math.sqrt(pr.vx * pr.vx + pr.vy * pr.vy);
                if (spd > 4) { pr.vx = pr.vx / spd * 4; pr.vy = pr.vy / spd * 4; }
            }
            pr.x += pr.vx; pr.y += pr.vy;
            if (pr.life <= 0) { this.projectiles.splice(i, 1); continue; }
            if (Math.abs(pr.x - px) < 10 && Math.abs(pr.y - py) < 14 && player.iframes <= 0) {
                player.hp = Math.max(0, player.hp - pr.dmg); player.iframes = 400; player.dmgFlash = 200;
                player.vx += (pr.vx > 0 ? 3 : -3); player.vy -= 2; sfx.hurt(); game.addShake(3, 60);
                game.dmgNums.add(px, player.y - 5, pr.dmg, '#44ffcc');
                this.projectiles.splice(i, 1);
            }
        }
        // Contact dmg from parts
        for (const p of parts) {
            if (p.hp <= 0) continue;
            const ew = 20, eh = 18;
            if (p.x - ew / 2 < player.x + PW && p.x + ew / 2 > player.x && p.y - eh / 2 < player.y + PH && p.y + eh / 2 > player.y && player.iframes <= 0) {
                player.hp = Math.max(0, player.hp - 10); player.iframes = 500; player.dmgFlash = 200;
                const kx = px > p.x ? 4 : -4; player.vx += kx; player.vy -= 3; sfx.hurt(); game.addShake(3, 60);
                game.dmgNums.add(px, player.y - 5, 10, '#44ffcc');
            }
        }
    }
    takeDamage(dmg, game, hitX, hitY) {
        if (this.state === 'dying' || !this.alive) return false;
        // Find closest alive part to hit
        let best = null, bestD = Infinity;
        const parts = [this.head, this.lHand, this.rHand];
        if (this.coreVuln) {
            if (this.core.iframes <= 0 && this.core.hp > 0) {
                this.core.hp = Math.max(0, this.core.hp - dmg);
                this.core.hitTimer = 250; this.core.iframes = 200;
                sfx.bossHit(); game.addShake(3, 60);
                game.dmgNums.add(this.cx, this.cy - 10, dmg, '#44ffcc');
                for (let i = 0; i < 5; i++) game.addPart(this.cx, this.cy, (Math.random() - .5) * 5, (Math.random() - .5) * 5, '#44ffcc', 25);
                return true;
            }
            return false;
        }
        for (const p of parts) {
            if (p.hp <= 0) continue;
            const d = Math.sqrt((hitX - p.x) ** 2 + (hitY - p.y) ** 2);
            if (d < bestD) { bestD = d; best = p; }
        }
        if (best && best.iframes <= 0) {
            best.hp = Math.max(0, best.hp - dmg);
            best.hitTimer = 250; best.iframes = 200;
            sfx.bossHit(); game.addShake(2, 50);
            game.dmgNums.add(best.x, best.y - 10, dmg, '#44ffcc');
            for (let i = 0; i < 4; i++) game.addPart(best.x, best.y, (Math.random() - .5) * 5, (Math.random() - .5) * 5, '#2288aa', 25);
            if (best.hp <= 0) { sfx.bossPhaseChange(); game.addShake(6, 200); }
            return true;
        }
        return false;
    }
    render(ctx, time) {
        if (!this.alive) return;
        const dying = this.state === 'dying';
        const dA = dying ? Math.max(0, 1 - this.deathTimer / 3000) : 1;
        // Tint overlay
        if (this.tint > 0.01 && !dying) {
            ctx.save(); ctx.globalAlpha = this.tint * 0.15;
            ctx.fillStyle = '#003322';
            ctx.fillRect(this.cx - 600, this.cy - 400, 1200, 800);
            ctx.restore();
        }
        // Trail
        for (const t of this.trailParts) { ctx.globalAlpha = (t.life / 50) * 0.35 * dA; ctx.fillStyle = t.c; ctx.fillRect(t.x - 2, t.y - 2, 4, 4); }
        ctx.globalAlpha = dA;
        // Body shape - torso
        const by = this.cy + Math.sin(this.floatOff) * 8;
        const glow = Math.sin(this.glowPulse) * 0.12 + 0.2;
        ctx.fillStyle = `rgba(30,120,100,${glow})`;
        ctx.beginPath(); ctx.ellipse(this.cx, by, 45, 65, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = dying ? 'rgba(20,60,55,0.6)' : '#183a35';
        ctx.beginPath(); ctx.ellipse(this.cx, by, 35, 55, 0, 0, Math.PI * 2); ctx.fill();
        // Tendrils connecting parts
        const parts = [this.head, this.lHand, this.rHand];
        ctx.strokeStyle = this.coreVuln ? 'rgba(68,255,204,0.5)' : 'rgba(34,136,170,0.4)';
        ctx.lineWidth = 3;
        for (const p of parts) {
            const mx = (this.cx + p.x) / 2 + Math.sin(time / 300 + p.ox) * 6;
            const my = (by + p.y) / 2 + Math.cos(time / 250 + p.ox) * 5;
            ctx.beginPath(); ctx.moveTo(this.cx, by); ctx.quadraticCurveTo(mx, my, p.x, p.y); ctx.stroke();
        }
        // Smaller tendrils
        ctx.strokeStyle = 'rgba(34,100,130,0.3)'; ctx.lineWidth = 1.5;
        for (let i = 0; i < 6; i++) {
            const a = i * Math.PI / 3 + Math.sin(time / 400 + i) * 0.3;
            const len = 30 + Math.sin(time / 200 + i * 2) * 8;
            ctx.beginPath(); ctx.moveTo(this.cx + Math.cos(a) * 30, by + Math.sin(a) * 45);
            ctx.lineTo(this.cx + Math.cos(a) * (30 + len) + Math.sin(time / 150 + i) * 4, by + Math.sin(a) * (45 + len) + Math.cos(time / 180 + i) * 4);
            ctx.stroke();
        }
        // Draw eyes (parts)
        for (const p of parts) {
            const fl = p.hitTimer > 0 && Math.floor(p.hitTimer / 40) % 2 === 0;
            const dead = p.hp <= 0;
            const ew = dead ? 16 : 20, eh = dead ? 14 : 18;
            // Socket glow
            if (!dead) { ctx.fillStyle = `rgba(68,220,200,${glow * 0.6})`; ctx.beginPath(); ctx.arc(p.x, p.y, ew * 0.8, 0, Math.PI * 2); ctx.fill(); }
            // Eye body
            ctx.fillStyle = fl ? '#fff' : (dead ? '#1a2a28' : '#1e4a44');
            ctx.beginPath(); ctx.ellipse(p.x, p.y, ew / 2, eh / 2, 0, 0, Math.PI * 2); ctx.fill();
            if (!dead) {
                // Sclera
                ctx.fillStyle = fl ? '#dff' : '#c8e8e4';
                ctx.beginPath(); ctx.ellipse(p.x, p.y, 7, 6, 0, 0, Math.PI * 2); ctx.fill();
                // Iris
                ctx.fillStyle = fl ? '#aff' : '#22ccaa';
                ctx.beginPath(); ctx.arc(p.x + p.pupilOff.x * 0.4, p.y + p.pupilOff.y * 0.4, 5, 0, Math.PI * 2); ctx.fill();
                // Pupil
                ctx.fillStyle = '#041510';
                ctx.beginPath(); ctx.ellipse(p.x + p.pupilOff.x * 0.5, p.y + p.pupilOff.y * 0.5, 2.5, 3, 0, 0, Math.PI * 2); ctx.fill();
                // HP bar
                if (p.hp < p.maxHp) { ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(p.x - 12, p.y - eh / 2 - 6, 24, 3); ctx.fillStyle = '#44ffcc'; ctx.fillRect(p.x - 12, p.y - eh / 2 - 6, 24 * (p.hp / p.maxHp), 3); }
            } else {
                // Dead socket - X marks
                ctx.strokeStyle = '#0a2020'; ctx.lineWidth = 2;
                ctx.beginPath(); ctx.moveTo(p.x - 4, p.y - 3); ctx.lineTo(p.x + 4, p.y + 3); ctx.moveTo(p.x + 4, p.y - 3); ctx.lineTo(p.x - 4, p.y + 3); ctx.stroke();
            }
        }
        // Core
        const cOpen = this.coreVuln ? Math.min(1, this.core.open + 0.01) : 0;
        this.core.open = cOpen;
        if (cOpen > 0.05) {
            const cfl = this.core.hitTimer > 0 && Math.floor(this.core.hitTimer / 40) % 2 === 0;
            const cPulse = Math.sin(this.core.pulse) * 0.3 + 0.7;
            ctx.fillStyle = `rgba(68,255,200,${0.15 * cPulse * cOpen})`;
            ctx.beginPath(); ctx.arc(this.cx, by, 22 * cOpen, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = cfl ? '#fff' : '#115544';
            ctx.beginPath(); ctx.arc(this.cx, by, 12 * cOpen, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = cfl ? '#ffe' : '#44ffcc';
            ctx.beginPath(); ctx.arc(this.cx, by, 7 * cOpen * cPulse, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = '#aaffee';
            ctx.beginPath(); ctx.arc(this.cx - 2, by - 2, 2 * cOpen, 0, Math.PI * 2); ctx.fill();
            if (this.core.hp < this.core.maxHp) { ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(this.cx - 15, by - 20, 30, 3); ctx.fillStyle = '#44ffcc'; ctx.fillRect(this.cx - 15, by - 20, 30 * (this.core.hp / this.core.maxHp), 3); }
        }
        // Laser charge & beam
        if (this.laser.chargeT > 0 && !this.laser.active) {
            const ch = this.laser.chargeT;
            ctx.strokeStyle = `rgba(68,255,200,${ch * 0.6})`; ctx.lineWidth = 2 + ch * 4;
            ctx.beginPath(); ctx.arc(this.head.x, this.head.y, 8 + ch * 10, 0, Math.PI * 2); ctx.stroke();
            for (let i = 0; i < 3; i++) {
                const a = this.head.angle + i * Math.PI * 2 / 3 + time / 200;
                ctx.fillStyle = `rgba(68,255,200,${ch * 0.4})`; ctx.beginPath(); ctx.arc(this.head.x + Math.cos(a) * (15 - ch * 8), this.head.y + Math.sin(a) * (15 - ch * 8), 2, 0, Math.PI * 2); ctx.fill();
            }
        }
        if (this.laser.active) {
            const la = this.laser.angle;
            const lw = 4 + Math.sin(time / 30) * 2;
            const grd = ctx.createLinearGradient(this.laser.src.x, this.laser.src.y, this.laser.src.x + Math.cos(la) * 300, this.laser.src.y + Math.sin(la) * 300);
            grd.addColorStop(0, 'rgba(68,255,200,0.9)'); grd.addColorStop(0.5, 'rgba(34,200,170,0.6)'); grd.addColorStop(1, 'rgba(0,100,80,0.1)');
            ctx.strokeStyle = grd; ctx.lineWidth = lw;
            ctx.beginPath(); ctx.moveTo(this.laser.src.x, this.laser.src.y); ctx.lineTo(this.laser.src.x + Math.cos(la) * 300, this.laser.src.y + Math.sin(la) * 300); ctx.stroke();
            ctx.strokeStyle = 'rgba(150,255,230,0.5)'; ctx.lineWidth = lw * 2.5;
            ctx.beginPath(); ctx.moveTo(this.laser.src.x, this.laser.src.y); ctx.lineTo(this.laser.src.x + Math.cos(la) * 300, this.laser.src.y + Math.sin(la) * 300); ctx.stroke();
        }
        // Projectiles
        for (const pr of this.projectiles) {
            ctx.globalAlpha = Math.min(1, pr.life / 20) * dA;
            ctx.fillStyle = pr.c;
            ctx.beginPath(); ctx.arc(pr.x, pr.y, pr.r, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.beginPath(); ctx.arc(pr.x - 1, pr.y - 1, pr.r * 0.4, 0, Math.PI * 2); ctx.fill();
        }
        ctx.globalAlpha = 1;
    }
}